import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Link, Stack, Tab, Table, TableProvider, Tabs, useTableState } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { LocalizedDateFromUtc, SpacedDash } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { compactArray } from '@mews/fp';
import { getReservationGroupName } from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector } from '@mews-commander/utils';
import { getAvailabilityBlockGroupData, ReservationGroupData } from '../../../selectors';

const GroupName = ({ groupId }: { groupId: string }) => {
    const groupName = useCommanderSelector((state) => getReservationGroupName(state, { id: groupId }));
    return <>{groupName}</>;
};

const columnHelper = createColumnHelper<ReservationGroupData>();
const getTableColumns = (openGroup: (groupId: string) => void) => [
    columnHelper.display({
        header: () => <LocalizedText textKey={TextKey.DateRange} />,
        id: 'interval',
        cell: (props) => (
            <span>
                <LocalizedDateFromUtc utcString={props.row.original.from} />
                {SpacedDash}
                <LocalizedDateFromUtc utcString={props.row.original.to} />
            </span>
        ),
    }),
    columnHelper.accessor('count', {
        header: () => <LocalizedText textKey={TextKey.NumberOfReservations} />,
        meta: { align: 'end' },
    }),
    columnHelper.accessor('groupId', {
        header: () => <LocalizedText textKey={TextKey.Group} />,
        cell: (props) => (
            <Link onClick={() => openGroup(props.getValue())} wordBreak="break-all" inherit>
                <GroupName groupId={props.row.original.groupId} />
            </Link>
        ),
    }),
];

const getReservationCount = (data: Array<ReservationGroupData>) => data.reduce((acc, { count }) => acc + count, 0);

const getInitialTab = (groupedTableData: AvailabilityBlockGroupsTableProps['groupedTableData']) => {
    if (groupedTableData.inhouse.length > 0) return 'inhouse';
    if (groupedTableData.future.length > 0) return 'future';
    if (groupedTableData.past.length > 0) return 'past';
    return 'canceled';
};
interface AvailabilityBlockGroupsTableProps {
    groupedTableData: ReturnType<typeof getAvailabilityBlockGroupData>;
    openGroup: (groupId: string) => void;
}

export const AvailabilityBlockGroupsTable = ({ groupedTableData, openGroup }: AvailabilityBlockGroupsTableProps) => {
    const [tableState, setTableState] = useTableState<ReservationGroupData>({ pagination: { show: false } });
    const [activeTab, setActiveTab] = useState<'inhouse' | 'future' | 'past' | 'canceled'>(() =>
        getInitialTab(groupedTableData)
    );
    const columns = useMemo(() => getTableColumns(openGroup), [openGroup]);

    const tableSource = groupedTableData[activeTab];

    return (
        <Stack spacing="200" vertical>
            <Tabs
                activeTabValue={activeTab}
                onTabClick={(_, value) => setActiveTab(value as 'future' | 'past' | 'inhouse' | 'canceled')}
                inline
            >
                {compactArray([
                    groupedTableData.inhouse.length > 0 ? (
                        <Tab
                            id="inhouse"
                            value="inhouse"
                            label={<LocalizedText textKey={TextKey.InHouse} />}
                            notificationsCount={getReservationCount(groupedTableData.inhouse)}
                        />
                    ) : null,
                    groupedTableData.future.length > 0 ? (
                        <Tab
                            id="future"
                            value="future"
                            label={<LocalizedText textKey={TextKey.Future} />}
                            notificationsCount={getReservationCount(groupedTableData.future)}
                        />
                    ) : null,
                    groupedTableData.past.length > 0 ? (
                        <Tab
                            id="past"
                            value="past"
                            label={<LocalizedText textKey={TextKey.Past} />}
                            notificationsCount={getReservationCount(groupedTableData.past)}
                        />
                    ) : null,
                    groupedTableData.canceled.length > 0 ? (
                        <Tab
                            id="canceled"
                            value="canceled"
                            label={<LocalizedText textKey={TextKey.Canceled} />}
                            notificationsCount={getReservationCount(groupedTableData.canceled)}
                        />
                    ) : null,
                ])}
            </Tabs>
            <TableProvider state={tableState} columns={columns} onStateChange={setTableState}>
                <Table uniqueRowId="groupId" data={tableSource} amountOfRows={tableSource.length} />
            </TableProvider>
        </Stack>
    );
};
